import clsx from 'clsx';
import { MoreVertical, Lock, Pencil, KeyRound, Trash2, ShieldCheck, User as UserIcon } from 'lucide-react';

import { IconButton } from '@/components/ui/icon-button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { AdminUser, AdminUserRole } from '@/features/admin/api/AdminApiClient';

type T = (key: string) => string;

function RoleBadge({ role, t }: { role: AdminUserRole; t: T }) {
  const label =
    role === 'super-admin'
      ? t('administration.roles.superAdmin')
      : role === 'admin'
        ? t('administration.roles.admin')
        : t('administration.roles.user');
  const Icon = role === 'user' ? UserIcon : ShieldCheck;
  const tone: BadgeTone = role === 'super-admin' ? 'warning' : role === 'admin' ? 'info' : 'neutral';
  return (
    <Badge tone={tone}>
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  );
}

function ModuleChips({ modules, t }: { modules: string[]; t: T }) {
  if (modules.length === 0) {
    return <span className="text-xs italic text-muted-foreground">{t('administration.members.noModules')}</span>;
  }
  return (
    <div className="flex max-w-[260px] flex-wrap gap-1">
      {modules.map((key) => (
        <Badge key={key} tone="primary">
          {t(`modules.labels.${key}`)}
        </Badge>
      ))}
    </div>
  );
}

export type MemberRowProps = {
  user: AdminUser;
  isSelf: boolean;
  /** Overrides the default protection rule (e.g. admin callers manage only user-level rows). */
  locked?: boolean;
  onEdit: (u: AdminUser) => void;
  onReset: (u: AdminUser) => void;
  onDelete: (u: AdminUser) => void;
  t: T;
};

export function MemberRow({ user, isSelf, locked, onEdit, onReset, onDelete, t }: MemberRowProps) {
  const isSuper = user.role === 'super-admin';
  const isProtected = locked ?? (isSuper && !isSelf);
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;

  return (
    <tr className={clsx('border-b border-border last:border-b-0', isSuper && !isSelf && 'bg-muted/40')}>
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{name}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {user.email}
          {isSelf && (
            <Badge tone="primary" size="sm" caps className="ml-1.5 font-sans">
              {t('administration.members.you')}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3"><RoleBadge role={user.role} t={t} /></td>
      <td className="px-4 py-3 text-foreground">{user.companyName}</td>
      <td className="px-4 py-3"><ModuleChips modules={user.modules} t={t} /></td>
      <td className="px-4 py-3 text-muted-foreground">{user.lastLogin ?? '—'}</td>
      <td className="px-4 py-3 text-right">
        {isProtected ? (
          <span
            title={t(
              isSuper ? 'administration.members.lockedSuper' : 'administration.members.lockedScope'
            )}
            aria-label={t('administration.members.locked')}
          >
            <Lock className="ml-auto h-4 w-4 text-warning" aria-hidden />
          </span>
        ) : (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <IconButton icon={MoreVertical} size="icon" label={t('administration.members.actions')} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(user)}>
                <Pencil className="h-4 w-4" />
                {t('administration.member.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onReset(user)}>
                <KeyRound className="h-4 w-4" />
                {t('administration.member.resetPassword')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onDelete(user)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
                {t('administration.member.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
}
